"use client";

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  TicketCheck,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { StatusBadge } from "~/components/dashboard/StatusBadge";
import { SurfaceCard } from "~/components/dashboard/SurfaceCard";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Paragraph, Title } from "~/components/ui/Typography";
import type { RouterOutputs } from "~/trpc/react";

type SupportTicket = RouterOutputs["supportTickets"]["findAll"][number];

const CATEGORY_LABELS: Record<string, string> = {
  access: "Access",
  billing: "Billing",
  listing: "Listing",
  safety: "Safety",
  account: "Account",
  other: "Other",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function TicketRow({
  ticket,
  onClick,
  isLast,
}: {
  ticket: SupportTicket;
  onClick: () => void;
  isLast: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full text-left transition-colors hover:bg-[color:var(--color-surface-2)] ${
        isLast ? "" : "border-b border-[color:var(--color-border)]"
      }`}
    >
      <div className="flex items-center gap-3 px-4 py-4 sm:gap-4 sm:px-6">
        <div className="grid size-10 shrink-0 place-items-center rounded-[12px] border border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] transition-transform group-hover:scale-105 sm:size-11">
          <TicketCheck className="size-4 text-[color:var(--color-muted)] sm:size-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[13px] font-semibold text-[color:var(--color-body)] sm:text-[14px]">
              {ticket.subject}
            </span>
          </div>

          <div className="mt-1.5 flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[11px] text-[color:var(--color-muted)] sm:text-[12px]">
                {CATEGORY_LABELS[ticket.category] ?? ticket.category}
              </span>
              <span className="flex items-center gap-1 text-[11px] text-[color:var(--color-muted)] sm:gap-1.5 sm:text-[12px]">
                <Clock className="size-3 shrink-0" />
                {formatDate(ticket.created_at)}
              </span>
            </div>
            <div className="shrink-0">
              <StatusBadge
                status={
                  ticket.status as Parameters<typeof StatusBadge>[0]["status"]
                }
              />
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function TicketDetailModal({
  ticket,
  onClose,
}: {
  ticket: SupportTicket | null;
  onClose: () => void;
}) {
  const isResolved =
    ticket?.status === "resolved" || ticket?.status === "closed";

  return (
    <Dialog open={!!ticket} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex flex-col gap-0 overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-0 text-[color:var(--color-body)]"
        aria-describedby={undefined}
      >
        {ticket && (
          <>
            <DialogHeader className="border-b border-[color:var(--color-border)] px-6 pb-5 pt-6">
              <DialogTitle className="text-[15px] font-semibold text-[color:var(--color-body)]">
                Case details
              </DialogTitle>
              <p className="mt-0.5 text-[12px] text-[color:var(--color-muted)]">
                Opened {formatDate(ticket.created_at)}
              </p>
            </DialogHeader>

            <div className="flex max-h-[60vh] flex-col gap-5 overflow-y-auto px-6 py-5">
              {isResolved ? (
                <div className="flex items-start gap-3 rounded-[10px] border border-[color:var(--green-bg)] bg-[color:var(--green-bg)] px-4 py-3">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--green)]" />
                  <p className="text-[13px] leading-snug text-[color:var(--green)]">
                    This case has been resolved. If you still need help, please
                    open a new support request.
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-[10px] border border-[color:var(--amber-bg)] bg-[color:var(--amber-bg)] px-4 py-3">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--amber)]" />
                  <p className="text-[13px] leading-snug text-[color:var(--amber)]">
                    Our support team is reviewing your case. We&apos;ll reach
                    out if we need any additional information.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <p className="text-[12px] font-semibold text-[color:var(--color-secondary)]">
                  Reason submitted
                </p>
                <div className="rounded-[8px] border border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] px-3 py-2.5 text-[13px] text-[color:var(--color-body)]">
                  {ticket.subject}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <p className="text-[12px] font-semibold text-[color:var(--color-secondary)]">
                  Additional details
                </p>
                <div className="min-h-[64px] whitespace-pre-wrap rounded-[8px] border border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] px-3 py-2.5 text-[13px] text-[color:var(--color-muted)]">
                  {ticket.description?.trim()
                    ? ticket.description
                    : "None provided"}
                </div>
              </div>

              {ticket.resolved_at && (
                <div className="flex flex-col gap-1.5">
                  <p className="text-[12px] font-semibold text-[color:var(--color-secondary)]">
                    Resolved
                  </p>
                  <p className="text-[13px] text-[color:var(--color-body)]">
                    {formatDate(ticket.resolved_at)}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end border-t border-[color:var(--color-border)] px-6 py-4">
              <Button type="button" variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function MyCasesSection({ tickets }: { tickets: SupportTicket[] }) {
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null,
  );

  // Tickets are sorted by created_at descending so the most recently opened
  // case appears at the top.
  const sortedTickets = useMemo(
    () =>
      [...tickets].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [tickets],
  );

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;
  const totalPages = Math.max(1, Math.ceil(sortedTickets.length / PAGE_SIZE));
  const paginatedTickets = sortedTickets.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | string)[]>((acc, p, idx, arr) => {
      if (idx > 0 && p - (arr[idx - 1] as number) > 1) {
        acc.push(`ellipsis-${arr[idx - 1]}`);
      }
      acc.push(p);
      return acc;
    }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-clamp on count changes
  useEffect(() => {
    setPage(1);
  }, [sortedTickets.length]);

  return (
    <>
      <SurfaceCard className="flex flex-col overflow-hidden p-0">
        <div className="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TicketCheck
                size={18}
                className="text-[color:var(--color-accent)]"
              />
              <Title className="text-[16px] text-[color:var(--color-body)]">
                My cases
              </Title>
            </div>
            <div className="flex items-center gap-2">
              <Link href="/support/new">
                <Button
                  type="button"
                  size="sm"
                  className="flex items-center gap-1.5"
                >
                  <Plus size={13} />
                  New ticket
                </Button>
              </Link>
            </div>
          </div>
          <Paragraph className="mt-1.5 text-[12px] text-[color:var(--color-secondary)]">
            Track the status of your open issues and support requests.
          </Paragraph>
        </div>

        {tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--color-surface-2)]">
              <TicketCheck
                size={24}
                className="text-[color:var(--color-muted)]"
              />
            </div>
            <p className="text-[14px] font-semibold text-[color:var(--color-body)]">
              No cases yet
            </p>
            <p className="max-w-[200px] text-[13px] text-[color:var(--color-muted)]">
              Raise a ticket or report an issue from a reservation and it will
              appear here.
            </p>
          </div>
        ) : (
          <>
            {paginatedTickets.map((ticket, i) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                onClick={() => setSelectedTicket(ticket)}
                isLast={i === paginatedTickets.length - 1}
              />
            ))}

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-[color:var(--color-border)] px-4 py-3 sm:px-6">
                <p className="text-[12px] text-[color:var(--color-muted)]">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="grid size-8 place-items-center rounded-[6px] text-[color:var(--color-muted)] transition-colors hover:bg-[color:var(--color-surface-2)] hover:text-[color:var(--color-body)] disabled:pointer-events-none disabled:opacity-40"
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  {pageNumbers.map((item) =>
                    typeof item === "string" ? (
                      <span
                        key={item}
                        className="grid size-8 place-items-center text-[12px] text-[color:var(--color-muted)]"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setPage(item)}
                        className={`grid size-8 place-items-center rounded-[6px] text-[12px] font-medium transition-colors ${
                          page === item
                            ? "bg-[color:var(--color-accent)]/10 text-[color:var(--color-accent)]"
                            : "text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface-2)] hover:text-[color:var(--color-body)]"
                        }`}
                      >
                        {item}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="grid size-8 place-items-center rounded-[6px] text-[color:var(--color-muted)] transition-colors hover:bg-[color:var(--color-surface-2)] hover:text-[color:var(--color-body)] disabled:pointer-events-none disabled:opacity-40"
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </SurfaceCard>

      <TicketDetailModal
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
      />
    </>
  );
}
